import { useEffect, useRef, useState } from 'react'

import type { RefObject } from 'react'

type Size = {
  width: number | undefined
  height: number | undefined
}

type UseResizeObserverOptions<T extends HTMLElement | null = HTMLElement> = {
  ref: RefObject<T>
  onResize?: (size: Size) => void
  box?: 'border-box' | 'content-box' | 'device-pixel-content-box'
}

const initialSize: Size = {
  width: undefined,
  height: undefined,
}

function useResizeObserver<T extends HTMLElement | null = HTMLElement>(options: UseResizeObserverOptions<T>): Size {
  const { ref, box = 'content-box' } = options
  const [{ width, height }, setSize] = useState<Size>(initialSize)
  const previousSize = useRef<Size>({ ...initialSize })
  const onResize = useRef<((size: Size) => void) | undefined>(undefined)
  onResize.current = options.onResize

  useEffect(() => {
    if (!ref || !ref.current) return

    if (!window || typeof window === 'undefined' || !('ResizeObserver' in window)) return

    const observer = new ResizeObserver(([entry]) => {
      if (!ref.current) return
      const boxProp =
        box === 'border-box'
          ? 'borderBoxSize'
          : box === 'device-pixel-content-box'
            ? 'devicePixelContentBoxSize'
            : 'contentBoxSize'

      if (!entry) return
      const newWidth = extractSize(entry, boxProp, 'inlineSize')
      const newHeight = extractSize(entry, boxProp, 'blockSize')

      const hasChanged = previousSize.current.width !== newWidth || previousSize.current.height !== newHeight

      if (hasChanged) {
        const newSize: Size = { width: newWidth, height: newHeight }
        previousSize.current.width = newWidth
        previousSize.current.height = newHeight

        if (onResize.current) {
          onResize.current(newSize)
        } else {
          setSize(newSize)
        }
      }
    })

    observer.observe(ref.current, { box })
    const size = { width: ref.current.clientWidth, height: ref.current.clientHeight }
    setSize(size)
    if (onResize.current) {
      onResize.current(size)
    }

    return () => {
      observer.disconnect()
    }
  }, [box, ref])

  return { width, height }
}

type BoxSizesKey = keyof Pick<ResizeObserverEntry, 'borderBoxSize' | 'contentBoxSize' | 'devicePixelContentBoxSize'>

function extractSize(
  entry: ResizeObserverEntry,
  box: BoxSizesKey,
  sizeType: 'inlineSize' | 'blockSize'
): number | undefined {
  const boxSize = entry[box] as ResizeObserverSize[] | ResizeObserverSize | undefined

  if (Array.isArray(boxSize) && boxSize.length > 0) {
    return boxSize[0]?.[sizeType]
  }

  if (boxSize && typeof boxSize === 'object' && sizeType in boxSize) {
    return (boxSize as ResizeObserverSize)[sizeType]
  }

  // Fallback to contentRect for better browser support
  return sizeType === 'inlineSize' ? entry.contentRect.width : entry.contentRect.height
}

export default useResizeObserver
